import { useCallback, useEffect, useState } from "react";
import { readSession, type SessionState } from "./session";

/**
 * The session, loaded once on mount and refreshed after anything that changes it.
 *
 * **This hook is what makes "stays signed in across a browser restart" true in the browser.** The
 * cookie surviving the restart is only half of it — the application starts with no state at all, so
 * its first act has to be asking the API who it is talking to. Without this, a returning member of
 * staff holds a perfectly good 90-day session and is shown the sign-in form anyway.
 *
 * `loading` is a third state rather than defaulting to signed-out, and it matters more than it looks:
 * rendering the sign-in form for the few hundred milliseconds the check takes would make every page
 * load flash a form at somebody who is already signed in.
 *
 * No context provider and no state library. One consumer, one request, at the root of the tree — and
 * the moment a second component needs this, a provider is the right answer rather than a second call.
 */
export interface Session {
  state: SessionState | undefined;
  loading: boolean;
  /** The request failed outright — offline, or the API is down. Distinct from being signed out. */
  error: boolean;
  refresh: () => Promise<void>;
}

export function useSession(): Session {
  const [state, setState] = useState<SessionState | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  /**
   * The outcome of one lookup, applied in one place.
   *
   * A network failure is deliberately **not** treated as signed out. An unreachable API and an
   * expired session are different things, and sending somebody to a sign-in form that also cannot
   * reach the API is the least useful thing to do with the distinction.
   */
  const apply = useCallback((next: SessionState | undefined, failed: boolean) => {
    if (next) setState(next);
    setError(failed);
    setLoading(false);
  }, []);

  useEffect(() => {
    /**
     * `.then` rather than an `async` body with `await`, and `loading` initialised to `true` rather
     * than set here. Both are for React's `set-state-in-effect` rule, which rejects an effect that
     * reaches `setState` synchronously — a cascading render on every mount — and accepts one that
     * does so from a callback when an external system answers. Which is exactly what this is.
     *
     * `live` is not ceremony either: StrictMode mounts every effect twice in development, so without
     * it the first mount's response resolves against an unmounted tree.
     */
    let live = true;
    void readSession().then(
      (next) => {
        if (live) apply(next, false);
      },
      () => {
        if (live) apply(undefined, true);
      },
    );
    return () => {
      live = false;
    };
  }, [apply]);

  /**
   * What callers use after signing in or out. This one may show the spinner synchronously, because it
   * only ever runs from an event handler — never from an effect.
   */
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      apply(await readSession(), false);
    } catch {
      apply(undefined, true);
    }
  }, [apply]);

  return { state, loading, error, refresh };
}
